import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  computeHorizontalMenuPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

export interface ApInvoiceHeaderMenuItem {
  id: string;
  label: string;
  disabled?: boolean;
  onSelect: () => void;
}

export function ApInvoiceHeaderOverflowMenu(props: {
  items: ApInvoiceHeaderMenuItem[];
}): JSX.Element | null {
  const { items } = props;
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<AnchoredMenuPanelBox | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = buttonRef.current;
    if (trigger == null) {
      setBox(null);
      return;
    }
    setBox(
      computeHorizontalMenuPanel({
        anchorRect: trigger.getBoundingClientRect(),
        menuWidth: 280,
        horizontalAlign: "end",
        maxHeightCap: 360,
      }),
    );
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (buttonRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        ref={buttonRef}
        type="button"
        variant="secondary"
        aria-label="More invoice actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <MoreVertical className="h-4 w-4" aria-hidden="true" />
      </Button>
      {open && box != null
        ? createPortal(
            <div
              className="fixed overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{
                left: box.left,
                top: box.top,
                width: box.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              onPointerDown={(event) => event.stopPropagation()}
            >
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className={cn(
                    "block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50",
                    item.disabled ? "cursor-not-allowed text-slate-400 hover:bg-transparent" : null,
                  )}
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.disabled) {
                      return;
                    }
                    setOpen(false);
                    item.onSelect();
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
